import { unwrapResult } from '@reduxjs/toolkit';
import { sendBookingConfirmation } from 'store/v1/bookings/bookings.actions';
import { displayAlert, openModal } from 'store/v1/ui/ui.actions';

/**
 * Helper to send booking confirmation with automatic toast handling
 * and error case handling (conflicts, missing template).
 *
 * @param {number} bookingId - The booking ID to send confirmation for
 * @param {Function} dispatch - Redux dispatch function
 * @param {Object} options - Optional configuration
 * @param {string} options.workspaceSchema - Workspace schema for navigation
 * @param {Object} options.history - React Router history object for navigation
 * @returns {Promise} Promise that resolves when confirmation is sent
 */
export function sendBookingConfirmationWithToast(
  bookingId,
  dispatch,
  options = {}
) {
  const { workspaceSchema, history } = options;

  return dispatch(sendBookingConfirmation(bookingId))
    .then(unwrapResult)
    .then(() => {
      dispatch(
        openModal('SuccessFeedbackModal', {
          title: 'Booking confirmation sent!',
          description:
            'You have successfully sent the booking confirmation email.',
          actionLabel: 'Done',
          icon: '/images/icon_colour_send.svg'
        })
      );
    })
    .catch(error => {
      // ApiClient sets status directly on error object (see lib/api/ApiClient.js:62)
      const status = error?.status;

      if (status === 400) {
        // No booking confirmation template
        dispatch(
          openModal('AlertModal', {
            title: 'No booking confirmation template',
            description:
              'You cannot send a booking confirmation without a booking confirmation template. Please add a confirmation template to continue.',
            actionLabel: 'Go to confirmation template',
            onAction:
              workspaceSchema && history
                ? () =>
                    history.replace(
                      `/app/settings/team/${workspaceSchema}/confirmation-templates`
                    )
                : undefined
          })
        );
      } else {
        // Generic error
        dispatch(displayAlert('error', 'Failed to send booking update'));
      }
    });
}
